import { Circle, KonvaNodeEvents, Line } from 'react-konva';
import { makeCirclesFromPoints } from 'utils/konva/drawingHelpers';
import { useMediaState } from 'modules/Canvas/contexts/MediaProvider/MediaState';
import { usePolyEditing } from 'modules/Canvas/components/Canvas/hooks/usePolyEditing';
import { useMouseTracker } from 'modules/Canvas/components/Canvas/MouseTracker';
import { PolygonHandle } from 'modules/Canvas/components/Canvas/PolygonHandle';
import { flatten } from 'rambda';
import React, { useMemo, forwardRef, useContext, useRef, useCallback } from 'react';
import { AnyObject } from 'types';
import { useCanvasStore } from 'modules/Canvas/contexts/CanvasStoreContext';
import { CANVAS_INTERACTION_STATE } from 'utils/konva/types';
import { KonvaEventObject } from 'konva/types/Node';
import { getPolygonData } from 'modules/Canvas/components/Canvas/helpers/shapes';
import { observer } from 'mobx-react-lite';
import { StageContext } from 'modules/Canvas/contexts/StageContext';
import { RegionLabel } from 'components/Canvas/RegionLabel';
import { isNearPolyBorder, setCursor } from 'components/Canvas/helpers';
import { CanvasRegionAnnotation } from 'modules/Canvas/helpers/types';

/**
 * An x and y coordinate
 */
type PolygonTuple = [x: number, y: number];

interface PolygonProps extends KonvaNodeEvents {
  /** Array of tuples, `[[x,y], [x,y]]` */
  points: PolygonTuple[];
  showHandles?: boolean;
  scale: number;
  dash?: number[];
  fill?: string;
  stroke?: string;
  opacity?: number;
  text?: string;
  id?: string;
  zoomScale?: number;
  fillPatternImage?: HTMLImageElement;
}

const PolygonComponent: React.ForwardRefExoticComponent<PolygonProps> = forwardRef(
  (
    { points, zoomScale = 1, showHandles = false, scale, fillPatternImage, fill, stroke = '#eee', opacity = 0.5, text, ...restProps }: PolygonProps,
    ref: React.Ref<import('konva/types/shapes/Line').Line>,
  ) => {
    const { element } = useMediaState();
    const pts = useMemo(() => flatten<number>(element ? getPolygonProps(points, element) : points), [points, element]);
    const handles = useMemo(() => makeCirclesFromPoints(points, scale), [points]);

    const topmostPoint = useMemo(
      () => points.reduce((point, topPoint) => (point[1] < topPoint[1] ? point : topPoint), [Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY]),
      [points],
    );
    const inverseScale = 1 / zoomScale;
    return (
      <>
        <Line
          ref={ref}
          points={flatten(points) || pts}
          fill={fill}
          stroke={stroke}
          opacity={opacity}
          closed
          strokeWidth={2.5 / zoomScale / scale}
          hitStrokeWidth={5}
          lineJoin="round"
          {...restProps}
          {...(fillPatternImage && { fillPatternImage })}
          fillEnabled
        />

        {showHandles &&
          handles.map((circleProps, i) => (
            <Circle
              id={`${i}`}
              scale={{ x: inverseScale, y: inverseScale }}
              key={`${circleProps.x}_${circleProps.y}`}
              shadowBlur={2}
              {...circleProps}
            />
          ))}
        {text && (
          <RegionLabel
            label={{ name: text, value: undefined }}
            color={fill || stroke}
            x={topmostPoint[0]}
            y={topmostPoint[1] - 5 / zoomScale}
            width={100}
            height={30}
            scale={zoomScale * scale}
            cornerStyle="rounded"
          />
        )}
      </>
    );
  },
);
export const Polygon = observer(PolygonComponent);

const EditablePolygon_ = forwardRef<
  import('konva/types/shapes/Line').Line,
  PolygonProps & {
    item: CanvasRegionAnnotation;
    onPatchPolygon?: (item: CanvasRegionAnnotation, points?: CF.API.Polygon['points']) => void;
  }
>(({ points, scale, fill, stroke = '#eee', fillPatternImage, opacity = 0.5, text, item, zoomScale = 1, onPatchPolygon, ...restProps }, _ref) => {
  const [mouseEvent] = useMouseTracker();
  const { element } = useMediaState();
  const canvasStore = useCanvasStore();
  const isBeingEdited = canvasStore.selectedShape === item.key;

  const pts = useMemo(() => flatten<number>(element ? getPolygonProps(points, element) : points), [points, element]);

  const lineRef = useRef(null);

  const { container } = useContext(StageContext);

  const onEditComplete = useCallback(
    ({ points: newPoints }: { points: PolygonTuple[] }) => {
      const p = getPolygonData(
        newPoints,
        element as unknown as {
          naturalHeight: number;
          naturalWidth: number;
        },
      );

      if (onPatchPolygon) onPatchPolygon(item, p);
    },
    [item, element],
  );

  const {
    shapeProps: editedShapeProps,
    polygonEventHandlers,
    handleEventHandlers,
  } = usePolyEditing({
    container,
    points,
    refs: { shapeRef: lineRef },
    onComplete: onEditComplete,
    isSelected: isBeingEdited,
    mouseEvent,
    enableAddPoints: true,
  });

  const handleOnClick = useCallback(() => {
    if (item.concepts && item.concepts.length > 0) {
      canvasStore.setSelectedShape(item.key);
      canvasStore.setEditingItem(`${item.concepts[0].id}-${item.key}`);
    }
    if (canvasStore.interactionState === CANVAS_INTERACTION_STATE.EDITING) {
      canvasStore.editing_initiate(item);
    }
  }, [canvasStore, element, item]);

  const onMouseMove = useCallback((evt: KonvaEventObject<MouseEvent>) => {
    if (isNearPolyBorder(evt)) {
      setCursor(evt, 'pointer');
    } else {
      setCursor(evt, 'auto');
    }
  }, []);

  const topmostPoint = useMemo(
    () =>
      editedShapeProps.points.reduce(
        (point, topPoint) => (point[1] < topPoint[1] ? point : topPoint),
        [Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY],
      ),
    [editedShapeProps.points],
  );
  const inverseScale = 1 / zoomScale;
  return (
    <>
      <Line
        ref={lineRef}
        points={flatten(editedShapeProps.points) || pts}
        fill={fill}
        stroke={stroke}
        opacity={opacity}
        closed
        strokeWidth={2.5 / zoomScale / scale}
        id={item.key}
        lineJoin="round"
        {...restProps}
        {...polygonEventHandlers}
        onClick={handleOnClick}
        onMouseMove={onMouseMove}
        hitStrokeWidth={5}
        {...(fillPatternImage && { fillPatternImage })}
      />
      {((editedShapeProps?.handles || []) as unknown as HandleData[]).map((circleProps, idx: number) => (
        <PolygonHandle
          id={`${idx}`}
          // eslint-disable-next-line react/no-array-index-key
          key={`${idx}`}
          scale={{ x: inverseScale, y: inverseScale }}
          {...circleProps}
          onMouseDown={handleEventHandlers.handleGrabStart}
          onMouseUp={handleEventHandlers.handleGrabEnd}
        />
      ))}
      {text && (
        <RegionLabel
          opacity={isBeingEdited ? 0.5 : 1}
          label={{ name: text, value: undefined }}
          color={fill || stroke}
          x={topmostPoint[0]}
          y={topmostPoint[1] - 5 / zoomScale}
          width={100}
          height={30}
          scale={zoomScale * scale}
          cornerStyle="rounded"
        />
      )}
    </>
  );
});

export const EditablePolygon = observer(EditablePolygon_);

const canvasHeight = ({ naturalHeight, videoHeight }: AnyObject): number => naturalHeight || videoHeight;
const canvasWidth = ({ naturalWidth, videoWidth }: AnyObject): number => naturalWidth || videoWidth;

function getPolygonProps(points: [col: number, row: number][], media: AnyObject): [number, number][] {
  if (!media) return points;

  const { naturalHeight, naturalWidth, videoHeight, videoWidth } = media;
  const height = canvasHeight({ naturalHeight, videoHeight });
  const width = canvasWidth({ naturalWidth, videoWidth });
  const pts = points.map(([row, col]) => [col * width, row * height]) as [col: number, row: number][];

  return pts;
}

type HandleData = {
  x: number;
  y: number;
  strokeWidth: number;
  radius: number;
  fill: string;
};
